import React, { useEffect } from "react";
import { Placeholder } from "@bloomberg/media-ui-article";
import { createClasses } from "@bloomberg/media-ui-core";
import {
    QuickAddContainer,
    SecurityPillWithAnimation,
    Size,
    Variant,
} from "@bloomberg/media-ui-markets";
import { useFeatureGate } from "@foundation/statsig";

import { useRealTimeData } from "~hooks/useRealTimeData";
import { useTickerData } from "~hooks/useTickerData";
import { RequestStatus } from "~types/lineup";

import styles from "./TickerPill.module.css";

export interface TickerPillProps {
    className?: string;
    tickerId: string;
    storyIndex?: number;
    relatedIndex?: number;
}

export default function TickerPill({
    className,
    tickerId,
    storyIndex,
    relatedIndex,
}: TickerPillProps) {
    const { status, tickerData, setTickerData } = useTickerData({ tickerId });
    const featureGateEnabled = Boolean(
        useFeatureGate("watchlist_paywall")?.value ?? false
    );

    const realTimeData = useRealTimeData(tickerData?.realTimeTickerDescription);

    useEffect(() => {
        if (
            realTimeData &&
            realTimeData?.percentChange1Day !== tickerData?.percentChange1Day
        ) {
            setTickerData((previousTickerData) => {
                return {
                    ...previousTickerData,
                    ...realTimeData,
                };
            });
        }
    }, [realTimeData]);

    // Failed to request ticker data
    if (status === RequestStatus.Done && !tickerData) {
        return null;
    }

    return (
        <div
            data-component="ticker-pill"
            className={createClasses(className, styles.TickerPill)}
        >
            {status === RequestStatus.Pending && (
                <Placeholder>
                    <Placeholder.Image />
                </Placeholder>
            )}
            {status === RequestStatus.Done && tickerData && (
                <>
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a
                        data-link-index={storyIndex}
                        data-link-related-index={relatedIndex}
                        data-link-related-type="Ticker"
                        href={`/quote/${tickerData.id}`}
                        target="_blank"
                        rel="noopener"
                    >
                        <SecurityPillWithAnimation
                            percentChange={tickerData?.percentChange1Day}
                            size={Size.small}
                            ticker={tickerData.id?.replace(/:.*/, "")}
                            variant={Variant.refresh2024}
                        />
                    </a>
                    <QuickAddContainer
                        variant="icon"
                        ticker={tickerId}
                        disabled={!featureGateEnabled}
                    />
                </>
            )}
        </div>
    );
}
